import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHouse,
    faUser,
    faQuoteLeft,
    faEdit,
    faCalendarTimes,
    faFileArchive,
    faExplosion,
    faMoneyBill,
    faCog,
    faArrowsLeftRight,
    faChevronLeft,
    faChevronRight,
    faStar,
    faCar,
} from "@fortawesome/free-solid-svg-icons";

import { Link } from "preact-router";
import { useEffect, useState, useCallback } from "preact/hooks";
import {WithPermission} from "../permission/withPermission";
import {PERMISSIONS} from "../../lib/security/permissions";
import {ROLES} from "../../lib/security/roles";

const menuConfig = [
    {
        label: "Accueil",
        items: [{ href: "/", label: "Vue d'ensemble", icon: faHouse, roles: [], permissions: [] }],
        permissions: []
    },
    {
        label: "Gestion",
        items: [
            { href: "/customers", label: "Clients", icon: faUser, permissions:[PERMISSIONS.CUSTOMER_LIST] },
            { href: "/quotes", label: "Devis", icon: faQuoteLeft, disabled: true, permissions:[PERMISSIONS.QUOTE_LIST] },
            { href: "/policies", label: "Contrats", icon: faEdit, disabled: true, permissions:[PERMISSIONS.POLICY_LIST] },
            { href: "/claims", label: "Sinistres", icon: faCalendarTimes, disabled: true, permissions:[PERMISSIONS.CLAIM_LIST] },
            { href: "/supporting-documents", label: "Justificatifs", icon: faFileArchive, disabled: true, permissions:[PERMISSIONS.SUPPORTING_DOCUMENT_LIST] },
            { href: "/terminations", label: "Résiliations", icon: faExplosion, disabled: true, permissions: [PERMISSIONS.REQUEST_LIST] },
            { href: "/invoicings", label: "Facturation", icon: faMoneyBill, permissions:[PERMISSIONS.BILLING_NAVBAR_TAB] },
        ],
        permissions: []
    },
    {
        label: "Outils",
        items: [
            { href: "/vehicles", label: "Véhiculiers", icon: faCar, roles:[ROLES.BROKER] },
            { href: "/settings", label: "Paramètres", icon: faCog, roles: [ROLES.SUPER_ADMIN] }
        ],
        permissions: [],
        roles: [],
    },
];

export default function Sidebar ({
    isOpen = false,
    isMobile = false,
    isAnimated = true,
    currentPath,
    user,
    ...props
}) {
    const [isResizing, setIsResizing] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(220);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [bookmarkedItems, setBookmarkedItems] = useState([]);

    const loadItems = () => {
        const savedItems = localStorage.getItem("settingsItems");
        return savedItems ? JSON.parse(savedItems).filter((item) => item.bookmarked) : [];
    };

    const isItemActive = (item) => {

        if (item.href === "/") {
            return currentPath === "/";
        }

        return currentPath?.startsWith(item.href) && item.href !== "/";
    }

    const canViewTools = () => !user?.roles?.includes("wakam");

    const handleSidebarResize = useCallback(
        (e) => {
            if (isResizing) {
                let xClient = e.clientX;
                const minSidebarWidth = 150;
                const maxSidebarWidth = window.innerWidth * 0.5;
                xClient = Math.min(Math.max(xClient, minSidebarWidth), maxSidebarWidth);
                setSidebarWidth(xClient);
            }
        },
        [isResizing]
    );

    const handleSidebarStartResize = (e) => {
        e.preventDefault();
        setIsResizing(true);
    };

    const handleSidebarStopResize = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        document.body.style.cursor = isResizing ? "col-resize" : "default";
        if (isResizing) {
            document.addEventListener("mousemove", handleSidebarResize);
            document.addEventListener("mouseup", handleSidebarStopResize);
        } else {
            document.removeEventListener("mousemove", handleSidebarResize);
            document.removeEventListener("mouseup", handleSidebarStopResize);
        }

        return () => {
            document.removeEventListener("mousemove", handleSidebarResize);
            document.removeEventListener("mouseup", handleSidebarStopResize);
        };
    }, [isResizing, handleSidebarResize, handleSidebarStopResize]);

    useEffect(() => {
        const width = localStorage.getItem("sidebarWidth");
        if (width) setSidebarWidth(Number(width));
    }, []);

    useEffect(() => {
        if (!isResizing) localStorage.setItem("sidebarWidth", sidebarWidth);
    }, [sidebarWidth, isResizing]);

    useEffect(() => {
        setBookmarkedItems(loadItems());
    }, []);

    const renderMenuItems = (items) => {
        return  items.map((item) => (
            <WithPermission
                key={item.label}
                requiredRoles={item.roles ?? []}
                requiredPermissions={item.permissions ?? []}

            >
                <li key={item.label} className="nav-item">
                    {item.external || item?.href.startsWith("http") ? (
                        <a
                            href={item.href}
                            rel="noopener noreferrer"
                            target={item.target || item?.href.startsWith("http") ? "_blank" : "_self"}
                            className={`d-inline-flex text-decoration-none gap-2 ${
                                isItemActive(item.href) ? "active" : ""
                            }`}
                            disabled={item.disabled}
                        >
                            <FontAwesomeIcon icon={item.icon} />
                            {item.label}
                        </a>
                    ) : (
                        <Link
                            href={item.href}
                            className={`d-inline-flex text-decoration-none gap-2 ${
                                isItemActive(item.href) ? "active" : ""
                            }`}
                            disabled={item.disabled}
                        >
                            <FontAwesomeIcon icon={item.icon} />
                            {item.label}
                            {item.bookmarked && (
                                <FontAwesomeIcon
                                    icon={faStar}
                                    style={{color: "gold"}}
                                    onClick={() => toggleBookmark(item.label)}
                                />
                            )}
                        </Link>
                    )}
                </li>
            </WithPermission>

        ))
    }
    return (
        <>
            <div
                id="sidebar"
                className={`sidebar ${isMobile ? "is-mobile" : ""} ${
                    isAnimated ? "animate__animated animate__fadeInLeft" : ""
                }`}
                style={{
                    position: "relative",
                    zIndex: 1000,
                    width: `${sidebarWidth}px`,
                    display: sidebarOpen ? "block" : "none",
                }}
            >
                <ul className="list-unstyled ps-0">
                    {menuConfig.map((section) => (
                        <WithPermission
                            key={section.label}
                            requiredPermissions={section.permissions ?? []}
                            requiredRoles={section.roles ?? []}
                        >
                            <li className="">
                                <button
                                    className="btn btn-toggle d-inline-flex align-items-center"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#${section.label}-collapse`}
                                    aria-expanded="true"
                                >
                                    {section.label}
                                </button>
                                <div className="collapse show" id={`${section.label}-collapse`}>
                                    <ul className="btn-toggle-nav list-unstyled fw-normal small">
                                        {renderMenuItems(section.items)}
                                        {section.label === "Outils" && canViewTools() && renderMenuItems(bookmarkedItems)}
                                    </ul>
                                </div>
                            </li>
                        </WithPermission>
                    ))}
                </ul>
                <div
                    className={`sidebar-right-border ${isResizing ? "animate_animated animate__bounce animate__infinite" : ""}`}
                    style={{
                        cursor: "col-resize",
                        color: "rgba(200,200,200,0.15)",
                        height: "100%",
                        width: "10px",
                    }}
                    onMouseDown={handleSidebarStartResize}
                >
                    <FontAwesomeIcon icon={faArrowsLeftRight}/>
                </div>
            </div>
            <div
                className="sidebar-close-button"
                style={{
                    left: sidebarOpen ? `${sidebarWidth - 30}px` : "0px",
                    borderRadius: sidebarOpen ? "0px 0px 5px 0px" : "0px 5px 0px 0px",
                }}
                onClick={() => setSidebarOpen(!sidebarOpen)}
            >
                <FontAwesomeIcon icon={sidebarOpen ? faChevronLeft : faChevronRight}/>
            </div>
        </>
        );
        }


